import React from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Plus, Trash } from '@phosphor-icons/react';
import {
  confidenceRowClass,
  confidenceTextClass,
  entryHours,
  entryUnits,
} from './constants';

const VisitRow = ({ tsId, entry, editable, onUpdate, onRemove }) => {
  const units = entryUnits(entry);
  const hrs = entryHours(entry);
  const conf = entry.confidence;
  const confClass = confidenceRowClass(conf);
  return (
    <div
      className={`grid grid-cols-12 gap-2 items-center hover:bg-indigo-50/30 rounded-md px-3 py-2 border border-slate-100 ${confClass || 'bg-slate-50'}`}
      data-testid={`visit-${tsId}-${entry._idx}`}
      title={conf != null ? `AI confidence: ${conf}%` : undefined}
    >
      <div className="col-span-3">
        <Input
          value={entry.date || ''}
          onChange={(e) => onUpdate(entry._idx, 'date', e.target.value)}
          className="h-8 text-sm bg-white"
          placeholder="MM/DD/YYYY"
          disabled={!editable}
          data-testid={`entry-date-${tsId}-${entry._idx}`}
        />
      </div>
      <div className="col-span-1 text-xs text-slate-600 font-medium">
        {entry.day_of_week || '—'}
      </div>
      <div className="col-span-2">
        <Input
          value={entry.time_in || ''}
          onChange={(e) => onUpdate(entry._idx, 'time_in', e.target.value)}
          className="h-8 text-sm bg-white"
          placeholder="09:00 AM"
          disabled={!editable}
          data-testid={`entry-timein-${tsId}-${entry._idx}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          value={entry.time_out || ''}
          onChange={(e) => onUpdate(entry._idx, 'time_out', e.target.value)}
          className="h-8 text-sm bg-white"
          placeholder="05:00 PM"
          disabled={!editable}
          data-testid={`entry-timeout-${tsId}-${entry._idx}`}
        />
      </div>
      <div className="col-span-1 text-right text-sm font-medium text-slate-700">
        {hrs.toFixed(2)}
      </div>
      <div className="col-span-1 text-right text-sm font-semibold text-indigo-900">
        {units}
      </div>
      <div className="col-span-2 flex justify-end items-center gap-2">
        {conf != null && (
          <span
            className={`text-[10px] font-semibold tabular-nums ${confidenceTextClass(conf)}`}
            data-testid={`entry-confidence-${tsId}-${entry._idx}`}
          >
            {conf}%
          </span>
        )}
        {editable && (
          <button
            onClick={() => onRemove(entry._idx)}
            className="text-red-300 hover:text-red-600 transition-colors p-1"
            data-testid={`entry-delete-${tsId}-${entry._idx}`}
            aria-label="Delete visit"
          >
            <Trash size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

const VisitsSection = ({ tsId, entries, editable, onUpdate, onAdd, onRemove }) => (
  <div className="px-5 py-4">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-semibold text-slate-700">
        Visits ({entries.length})
      </h4>
      {editable && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onAdd}
          className="text-indigo-700 h-7 text-xs"
          data-testid={`add-visit-${tsId}`}
        >
          <Plus size={14} className="mr-1" /> Add Visit
        </Button>
      )}
    </div>

    {entries.length === 0 ? (
      <div className="text-center py-6 text-sm text-slate-400 bg-slate-50 rounded-md">
        No visits extracted from this timesheet. Click "Add Visit" to create one.
      </div>
    ) : (
      <div className="space-y-2">
        <div className="grid grid-cols-12 gap-2 px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
          <div className="col-span-3">Date</div>
          <div className="col-span-1">Day</div>
          <div className="col-span-2">Time In</div>
          <div className="col-span-2">Time Out</div>
          <div className="col-span-1 text-right">Hrs</div>
          <div className="col-span-1 text-right">Units</div>
          <div className="col-span-2" />
        </div>
        {entries.map((entry) => (
          <VisitRow
            key={entry._idx}
            tsId={tsId}
            entry={entry}
            editable={editable}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        ))}
      </div>
    )}
  </div>
);

export default VisitsSection;
